import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import styles from './CageProfile.module.css';

function CageProfile() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [cage, setCage] = useState(null);
    const [aviaries, setAviaries] = useState([]);
    const [availableBirds, setAvailableBirds] = useState([]);
    const [cageBirds, setCageBirds] = useState([]); // Aves na gaiola
    const [loading, setLoading] = useState(true);

    // Estados dos Alertas
    const [ovoscopyDone, setOvoscopyDone] = useState(false);
    const [hatchDone, setHatchDone] = useState(false);
    const [ringDone, setRingDone] = useState(false);

    // Sincroniza o status dos alertas quando a gaiola/postura ativa for carregada
    useEffect(() => {
        if (cage?.activePosture) {
            setOvoscopyDone(cage.activePosture.ovoscopy_done || false);
            setHatchDone(cage.activePosture.hatch_done || false);
            setRingDone(cage.activePosture.ring_done || false);
        }
    }, [cage]);

    // Alterna e salva o status dos checkboxes de alertas
    async function toggleAlertStatus(alertType, currentValue, setter) {
        const activePosture = cage?.activePosture;
        if (!activePosture?._id) return;

        const newValue = !currentValue;
        setter(newValue);

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.patch(`/posture/${activePosture._id}`, 
                { [alertType]: newValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            console.error(`Erro ao atualizar ${alertType}:`, error);
            setter(currentValue); // Reverte o checkbox em caso de erro na API
        }
    }

    // Modais
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isReproModalOpen, setIsReproModalOpen] = useState(false);
    const [isPostureModalOpen, setIsPostureModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);

    // Forms
    const [editForm, setEditForm] = useState({
        number: '',
        title: '',
        color: '',
        type: '',
        height: '',
        width: '',
        depth: '',
        aviaryId: ''
    });

    const [reproForm, setReproForm] = useState({ reproduction_start: '', status: 'Ativo' });
    const [postureForm, setPostureForm] = useState({
        posture_start: '',
        first_egg: '',
        last_egg: '',
        number_eggs: 0,
        male: '',
        female: '',
        comments: ''
    });

    const [offspringCount, setOffspringCount] = useState(0);
    const [finishFormData, setFinishFormData] = useState({
        posture_end: new Date().toISOString().split('T')[0],
        number_eggs: 0,
        comments: '',
    });
    const [offspringList, setOffspringList] = useState([]);

    useEffect(() => {
        fetchCageDetails();
        fetchAviaries();
        fetchBirds();
        fetchCageBirds();
    }, [id]);

    const initialPostureState = {
        posture_start: '',
        first_egg: '',
        last_egg: '',
        number_eggs: 0,
        male: '',
        female: '',
        comments: ''
    };

    const resetPostureForm = () => {
        setPostureForm(initialPostureState);
    };

    // Adiciona N dias a uma data em ISO/String
    function addDays(dateString, days) {
        if (!dateString) return null;
        const date = new Date(dateString);
        date.setDate(date.getDate() + days);
        return date;
    }

    // Formata para pt-BR em UTC
    function formatDate(date) {
        if (!date) return '';
        return new Date(date).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    }

    // Retorna se a data alvo já chegou ou passou
    function isDateReached(targetDate) {
        if (!targetDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(targetDate);
        target.setHours(0, 0, 0, 0);
        return today >= target;
    }
    
    // Formata a data para exibir apenas DD/MM
    function formatShortDate(date) {
        if (!date) return '';
        return new Date(date).toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            timeZone: 'UTC' 
        });
    }

    async function fetchCageDetails() {
        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get(`/cage/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = res.data.cage;
            setCage(data);

            setEditForm({
                number: data.number || '',
                title: data.title || '',
                color: data.color || '',
                type: data.type || '',
                height: data.dimensions?.height || '',
                width: data.dimensions?.width || '',
                depth: data.dimensions?.depth || '',
                aviaryId: data.aviary?._id || data.aviary || ''
            });
        } catch (err) {
            console.error('Erro ao buscar perfil da gaiola:', err);
        } finally {
            setLoading(false);
        }
    }

    async function fetchCageBirds() {
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get(`/cage/${id}/birds`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCageBirds(res.data.birds || []);
        } catch (err) {
            console.error('Erro ao buscar aves da gaiola:', err);
        }
    }

    async function handleFinishReproduction() {
        if (!activeRepro) return;

        const confirmFinish = window.confirm('Deseja realmente finalizar esta reprodução?');
        if (!confirmFinish) return;

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.patch(`/reproduction/finish/${activeRepro._id}`, {
                reproduction_end: new Date().toISOString().split('T')[0]
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Reprodução finalizada com sucesso!');
            fetchCageDetails();
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao finalizar reprodução.');
        }
    }

    async function fetchAviaries() {
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get('/aviary/myaviarys', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAviaries(res.data.aviary || res.data.aviaries || res.data.aviarys || []);
        } catch (err) {
            console.error('Erro ao buscar aviários:', err);
        }
    }

    async function fetchBirds() {
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get('/bird/mybirds', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableBirds(res.data.birds || []);
        } catch (err) {
            console.error('Erro ao buscar aves:', err);
        }
    }

    const checkOvoscopyNeeded = (lastEggDateStr) => {
        if (!lastEggDateStr) return { isNeeded: false, targetDate: null };
        const lastEggDate = new Date(lastEggDateStr);
        const targetDate = new Date(lastEggDate);
        targetDate.setDate(targetDate.getDate() + 5);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkTarget = new Date(targetDate);
        checkTarget.setHours(0, 0, 0, 0);

        return {
            isNeeded: today >= checkTarget,
            targetDate: targetDate.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
        };
    };

    async function handleUpdateCage(e) {
        e.preventDefault();
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.patch(`/cage/${cage._id}`, editForm, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Gaiola atualizada com sucesso!');
            setIsEditModalOpen(false);
            fetchCageDetails();
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao atualizar gaiola.');
        }
    }

    // Atualiza a data na linha do tempo da postura
    async function handleUpdatePostureDate(field, selectedDate) {
        const activePosture = cage?.activePosture;
        if (!activePosture?._id || !selectedDate) return;

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.patch(`/posture/${activePosture._id}`, {
                [field]: selectedDate
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            fetchCageDetails(); // Recarrega os dados para atualizar prazos e alertas
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao atualizar data da postura.');
        }
    }

    async function handleCreateReproduction(e) {
        e.preventDefault();
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.post('/reproduction/create', {
                ...reproForm,
                cage: cage._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            alert('Reprodução iniciada!');
            setIsReproModalOpen(false);
            fetchCageDetails();
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao criar reprodução.');
        }
    }

    async function handleCreatePosture(e) {
        e.preventDefault();
        if (!cage?.activeReproduction) return alert('Não há reprodução ativa nesta gaiola.');
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.post('/posture/create', {
                ...postureForm,
                reproduction: cage.activeReproduction._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            alert('Postura registrada!');
            resetPostureForm();
            setIsPostureModalOpen(false);
            fetchCageDetails();
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao registrar postura.');
        }
    }

    const handleOpenFinishModal = () => {
        const activePosture = cage?.activePosture;
        if (!activePosture) return;

        setFinishFormData({
            posture_end: activePosture.posture_end 
                ? new Date(activePosture.posture_end).toISOString().split('T')[0] 
                : new Date().toISOString().split('T')[0],
            number_eggs: activePosture.number_eggs || 0,
            comments: activePosture.comments || '',
        });
        setOffspringCount(0);
        setOffspringList([]);
        setIsFinishModalOpen(true);
    };

    const handleOffspringCountChange = (count) => {
        const num = Math.max(0, parseInt(count) || 0);
        setOffspringCount(num);

        const activePosture = cage?.activePosture;
        const defaultBirthday = activePosture?.first_offspring 
            ? new Date(activePosture.first_offspring).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0];

        const updatedList = Array.from({ length: num }, (_, index) => {
            return offspringList[index] || {
                name: `Filhote ${index + 1}`,
                bird_band_right: '',
                bird_band_left: '',
                color: '',
                race: '',
                gender: 'male',
                birthday: defaultBirthday
            };
        });
        setOffspringList(updatedList);
    };

    const handleOffspringChange = (index, field, value) => {
        const updated = [...offspringList];
        updated[index][field] = value;
        setOffspringList(updated);
    };

    const handleFinishSubmit = async (e) => {
        e.preventDefault();
        const activePosture = cage?.activePosture;

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.patch(`/posture/finish/${activePosture._id}`, {
                ...finishFormData,
                offsprings: offspringList
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Postura finalizada com sucesso!');
            setIsFinishModalOpen(false);
            fetchCageDetails();
        } catch (err) {
            alert(err.response?.data?.message || 'Erro ao finalizar postura.');
        }
    };

    if (loading) return <p className={styles.loading}>Carregando ficha da gaiola...</p>;
    if (!cage) return <p className={styles.empty_text}>Gaiola não encontrada.</p>;

    const activeRepro = cage.activeReproduction;
    const activePosture = cage.activePosture;
    const male = activePosture?.male;
    const female = activePosture?.female;
    const ovoscopyInfo = checkOvoscopyNeeded(activePosture?.last_egg);

    return (
        <div className={styles.container}>
            <button className={styles.back_button} onClick={() => navigate('/cage')}>
                ← Voltar para gaiolas
            </button>

            {/* Cabeçalho do Perfil */}
            <div className={styles.cage_detail_header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className={styles.cage_number_box_large}>
                        {String(cage.number).padStart(2, '0')}                    
                    </div>
                    <div>
                        <h3 style={{ margin: 0 }}>
                            {cage.title} - {cage.type.charAt(0).toUpperCase() + cage.type.slice(1)}
                        </h3>
                    </div>
                </div>
                <div className={styles.header_actions_right}>
                    <button className={styles.btn_edit} onClick={() => setIsEditModalOpen(true)}>
                        ✏️ Editar Gaiola
                    </button>

                </div>
            </div>

            {/* Ações Rápidas */}
            {(!activeRepro || !activePosture) && (
                <div className={styles.actions_bar}>
                    {!activeRepro && (
                        <button className={styles.btn_action} onClick={() => setIsReproModalOpen(true)}>
                            + Nova Reprodução
                        </button>
                    )}
                    {activeRepro && !activePosture && (
                        <>
                            <button className={styles.btn_action_secondary} onClick={() => { resetPostureForm(); setIsPostureModalOpen(true); }}>
                                + Nova Postura
                            </button>
                            <button className={styles.btn_action_danger || styles.btn_cancel} onClick={handleFinishReproduction}>
                                Finalizar Reprodução
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Aves na Gaiola */}
            <div className={styles.section_box}>
                <h3>🐦 Aves na Gaiola ({cageBirds.length})</h3>
                {cageBirds.length > 0 ? (
                    <div className={styles.couple_grid}>
                        {cageBirds.map((bird) => {
                            const genderSymbol = bird.gender === 'male' ? '♂' : bird.gender === 'female' ? '♀' : '?';
                            const genderColor = bird.gender === 'male' ? '#007bff' : bird.gender === 'female' ? '#ff69b4' : '#000000';

                            return (
                                <div key={bird._id} className={styles.parent_card}>
                                    <div className={styles.card_header} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ color: genderColor, fontWeight: 'bold', fontSize: '1.2rem' }}>
                                            {genderSymbol}
                                        </span>
                                        <strong>{bird.name} ({bird.bird_band_left || '-'} - {bird.bird_band_right || '-'}) </strong>
                                        <button className={styles.link_button} onClick={() => navigate(`/bird/${bird._id}`)} >
                                            →
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className={styles.empty_subtext}>Nenhuma ave cadastrada nesta gaiola no momento.</p>
                )}
            </div>

            {/* Casal da Postura */}
            <div className={styles.section_box}>
                <h3>💕 Casal da Postura</h3>
                <div className={styles.couple_grid}>
                    {/* MACHO */}
                    {(() => {
                        const maleSymbol = male?.gender === 'female' ? '♀' : male?.gender === 'male' ? '♂' : '?';
                        const maleColor = male?.gender === 'female' ? '#ff69b4' : male?.gender === 'male' ? '#007bff' : '#000000';
                        return (
                            <div className={styles.parent_card} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', width: '100%' }}>
                                <strong>
                                    <span style={{ color: maleColor, fontWeight: 'bold', fontSize: '1.2rem', marginRight: '4px' }}> {maleSymbol}</span> 
                                    {male?.name || 'Não definido'} • ({male?.bird_band_left || '-'} : {male?.bird_band_right || '-'})
                                    <span style={{ margin: 0, display: 'inline' }}></span>
                                    <br></br>
                                    <span style={{ margin: 0, display: 'inline' }}>{male?.race || '-'} • {male?.color || ''}</span>
                                </strong>
                                
                                {male?._id && (
                                    <button 
                                        className={styles.link_button} 
                                        onClick={() => navigate(`/bird/${male._id}`)}
                                        style={{ marginLeft: 'auto', display: 'inline-block' }}
                                    >
                                        Ver ficha da ave →
                                    </button>
                                )}
                            </div>
                        );
                    })()}

                    {/* FÊMEA */}
                    {(() => {
                        const femaleSymbol = female?.gender === 'male' ? '♂' : female?.gender === 'female' ? '♀' : '?';
                        const femaleColor = female?.gender === 'male' ? '#007bff' : female?.gender === 'female' ? '#ff69b4' : '#000000';
                        return (
                            <div className={styles.parent_card} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', width: '100%' }}>
                                <strong>
                                    <span style={{ color: femaleColor, fontWeight: 'bold', fontSize: '1.2rem', marginRight: '4px' }}>{femaleSymbol}</span> 
                                    {female?.name || 'Não definida'} • ({female?.bird_band_left || '-'} : {female?.bird_band_right || '-'})
                                    <span style={{ margin: 0, display: 'inline' }}></span>
                                    <br></br>
                                    <span style={{ margin: 0, display: 'inline' }}>{female?.race || '-'} • {female?.color || ''}</span>
                                </strong>
                                
                                {female?._id && (
                                    <button 
                                        className={styles.link_button} 
                                        onClick={() => navigate(`/bird/${female._id}`)}
                                        style={{ marginLeft: 'auto', display: 'inline-block' }}
                                    >
                                        Ver ficha da ave →
                                    </button>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* SEÇÃO DE ALERTAS VINCULADA À REPRODUÇÃO E POSTURA ATUAL */}
            {activeRepro && activePosture && (
                activePosture.choco_start || activePosture.first_offspring
            ) && (
                <div className={styles.section_box}>
                    <h3>📋 Alertas da Postura Atual</h3>

                    {/* 1. OVOSCOPIA: Só aparece se 'choco_start' estiver definido */}
                    {activePosture.choco_start && (
                        <div 
                            className={ovoscopyDone ? styles.alert_banner_success : (isDateReached(addDays(activePosture.choco_start, 6)) ? styles.alert_banner_warning : styles.alert_banner_info)} 
                            style={{ marginBottom: '0.8rem', padding: '0.8rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <div>
                                <strong>Ovoscopia - Janela</strong>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    <strong>De {formatShortDate(addDays(activePosture.choco_start, 6))}</strong> a <strong>{formatShortDate(addDays(activePosture.choco_start, 8))}</strong>
                                </p>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    (6º ao 8º dia de choco)
                                </p>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                                <input 
                                    type="checkbox" 
                                    checked={ovoscopyDone} 
                                    onChange={() => toggleAlertStatus('ovoscopy_done', ovoscopyDone, setOvoscopyDone)} 
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                {ovoscopyDone ? 'Concluído' : 'Marcar feita'}
                            </label>
                        </div>
                    )}

                    {/* 2. PREVISÃO DE NASCIMENTO: Só aparece se 'choco_start' estiver definido */}
                    {activePosture.choco_start && (
                        <div 
                            className={hatchDone ? styles.alert_banner_success : (isDateReached(addDays(activePosture.choco_start, 13)) ? styles.alert_banner_warning : styles.alert_banner_info)} 
                            style={{ marginBottom: '0.8rem', padding: '0.8rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <div>
                                <strong>Nascimento - Previsão</strong>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    <strong>Dia {formatShortDate(addDays(activePosture.choco_start, 13))}</strong>
                                </p>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    (13º dia de choco)
                                </p>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                                <input 
                                    type="checkbox" 
                                    checked={hatchDone} 
                                    onChange={() => toggleAlertStatus('hatch_done', hatchDone, setHatchDone)} 
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                {hatchDone ? 'Nascidos' : 'Marcar feito'}
                            </label>
                        </div>
                    )}

                    {/* 3. ANILHAMENTO: Só aparece se 'first_offspring' estiver definido */}
                    {activePosture.first_offspring && (
                        <div 
                            className={ringDone ? styles.alert_banner_success : (isDateReached(addDays(activePosture.first_offspring, 5)) ? styles.alert_banner_warning : styles.alert_banner_info)} 
                            style={{ padding: '0.8rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <div>
                                <strong>Anilhamento - Janela</strong>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    <strong>De {formatShortDate(addDays(activePosture.first_offspring, 5))}</strong> a <strong>{formatShortDate(addDays(activePosture.first_offspring, 8))}</strong>
                                </p>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                                    (5º ao 8º dia de vida)
                                </p>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                                <input 
                                    type="checkbox" 
                                    checked={ringDone} 
                                    onChange={() => toggleAlertStatus('ring_done', ringDone, setRingDone)} 
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                {ringDone ? 'Anilhados' : 'Marcar feito'}
                            </label>
                        </div>
                    )}
                </div>
            )}

            {/* Reprodução Atual */}
            {activeRepro && (() => {
                const currentPostures = activeRepro.postures || [];
                const totalEggs = currentPostures.reduce((acc, p) => acc + (p.number_eggs || 0), 0);
                const allCurrentOffspring = currentPostures.flatMap(p => p.offspring || []);
                const totalOffspring = allCurrentOffspring.length;
                const successRate = totalEggs > 0 ? Math.round((totalOffspring / totalEggs) * 100) : 0;

                return (
                    <div className={styles.section_box}>
                        <h3>📊 Reprodução Atual</h3>
                        <div className={styles.metrics_grid}>
                            <div className={styles.metric_card}>
                                <h2>{totalEggs}</h2>
                                <span>Ovos</span>
                            </div>
                            <div className={styles.metric_card}>
                                <h2>{totalOffspring}</h2>
                                <span>Filhotes</span>
                            </div>
                            <div className={styles.metric_card}>
                                <h2>{successRate}%</h2>
                                <span>Taxa de Sucesso</span>
                            </div>
                            <div className={styles.metric_card}>
                                <h2>{activeRepro.reproduction_start ? new Date(activeRepro.reproduction_start).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</h2>
                                <span>Início Preparação</span>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Linha do Tempo */}
            {activePosture && (
                <div className={styles.section_box}>
                    <h3>⏱️ Linha do Tempo Postura Atual</h3>
                    <div className={styles.timeline}>
                        {[
                            { key: 'posture_start', label: 'Início da postura', desc: 'Casal na gaiola de reprodução.' },
                            { key: 'first_egg', label: 'Primeiro ovo', desc: 'Primeiro ovo no ninho.' },
                            { key: 'last_egg', label: 'Último ovo', desc: 'Último ovo no ninho.' },
                            { key: 'choco_start', label: 'Início da choca', desc: 'Fêmea iniciou a choca.' },
                            { key: 'first_offspring', label: 'Nascimento filhote', desc: 'Filhote nasceu.' },
                            { key: 'posture_end', label: 'Anilhamento', desc: 'Anilhamento dos filhotes.' }
                        ].map((item) => (
                            <div key={item.key} className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture[item.key] ? new Date(activePosture[item.key]).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>{item.label}</h4>
                                    <p>{item.desc}</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button" 
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => e.currentTarget.nextElementSibling?.showPicker?.()}
                                        >
                                            {activePosture[item.key] ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            max={new Date().toISOString().split('T')[0]}
                                            className={styles.hidden_date_input}
                                            onChange={(e) => {
                                                const selectedDate = e.target.value;
                                                const today = new Date().toISOString().split('T')[0];

                                                if (selectedDate > today) {
                                                    alert('Não é permitido selecionar uma data futura.');
                                                    e.target.value = '';
                                                    return;
                                                }

                                                handleUpdatePostureDate(item.key, selectedDate);
                                            }}
                                        />
                                        {item.key === 'posture_end' && activePosture.posture_end && (
                                            <button type="button" className={styles.btn_finish_posture} onClick={handleOpenFinishModal}>
                                                Finalizar Postura
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Histórico da Reprodução Atual */}
            <div className={styles.section_box}>
                <h3>📋 Histórico de Posturas (Reprodução Atual)</h3>
                {activeRepro && activeRepro.postures?.length > 0 ? (
                    (() => {
                        const sortedPostures = [...activeRepro.postures].sort(
                            (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
                        );

                        return (
                            <div className={styles.history_card}>
                                {sortedPostures.map((posture, idx) => {
                                    const maleObj = typeof posture.male === 'object' 
                                        ? posture.male 
                                        : availableBirds.find(b => b._id === posture.male);

                                    const femaleObj = typeof posture.female === 'object' 
                                        ? posture.female 
                                        : availableBirds.find(b => b._id === posture.female);

                                    const offspringCount = posture.offspring?.length || posture.offsprings?.length || 0;

                                    return (
                                        <div key={posture._id || idx} className={styles.posture_history_item}>
                                            <div className={styles.posture_history_header}>
                                                <strong>Postura #{idx + 1}</strong>
                                                <span>Ovos: <b>{posture.number_eggs || 0}</b> | Filhotes: <b>{offspringCount}</b></span>
                                            </div>
                                            <div className={styles.posture_info_details}>
                                                <p>👥 <b>Casal:</b> {maleObj?.name || 'Macho não identificado'} × {femaleObj?.name || 'Fêmea não identificada'}</p>
                                                <p>📅 <b>Período: </b> 
                                                    {posture.posture_start ? new Date(posture.posture_start).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'N/A'} até {posture.posture_end ? new Date(posture.posture_end).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Em Andamento'}
                                                </p>
                                                
                                                {posture.comments && posture.comments.trim() !== '' && (
                                                    <p>📝 <b>Comentário: </b>{posture.comments}</p>
                                                )}
                                            </div>
                                            <br />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()
                ) : (
                    <p className={styles.empty_subtext}>Nenhuma postura registrada nesta reprodução.</p>
                )}
            </div>

            {/* Modal Editar Gaiola */}
            {isEditModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <h3>Editar Gaiola</h3>
                        <form onSubmit={handleUpdateCage} className={styles.form}>
                            <label>Aviário *</label>
                            <select 
                                value={editForm.aviaryId} 
                                onChange={(e) => setEditForm({...editForm, aviaryId: e.target.value})} 
                                required
                            >
                                <option value="">Selecione o Aviário</option>
                                {aviaries.map(a => (
                                    <option key={a._id} value={a._id}>{a.name}</option>
                                ))}
                            </select>

                            <label>Número *</label>
                            <input 
                                type="number" 
                                value={editForm.number} 
                                onChange={(e) => setEditForm({...editForm, number: e.target.value})} 
                                required 
                            />

                            <label>Título / Identificação *</label>
                            <input 
                                type="text" 
                                value={editForm.title} 
                                onChange={(e) => setEditForm({...editForm, title: e.target.value})} 
                                required 
                            />

                            <label>Tipo *</label>
                            <select name="gender" value={editForm.type} onChange={(e) => setEditForm({...editForm, type: e.target.value})} required>
                                <option value="criadeira">Criadeira</option>
                                <option value="voadeira">Voadeira</option>
                                <option value="individual">Individual</option>
                            </select>

                            <label>Cor *</label>
                            <input 
                                type="text" 
                                value={editForm.color} 
                                onChange={(e) => setEditForm({...editForm, color: e.target.value})} 
                                required 
                            />

                            <div className={styles.modal_actions}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className={styles.btn_cancel}>Cancelar</button>
                                <button type="submit" className={styles.btn_submit}>Salvar Alterações</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modais Adicionais (Reprodução, Postura e Finalização) */}
            {isReproModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <h3>Nova Reprodução</h3>
                        <form onSubmit={handleCreateReproduction} className={styles.form}>
                            <label>Data de Início *</label>
                            <input 
                                type="date" 
                                value={reproForm.reproduction_start} 
                                onChange={(e) => setReproForm({...reproForm, reproduction_start: e.target.value})} 
                                required 
                            />
                            <div className={styles.modal_actions}>
                                <button type="button" onClick={() => setIsReproModalOpen(false)} className={styles.btn_cancel}>Cancelar</button>
                                <button type="submit" className={styles.btn_submit}>Salvar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isPostureModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <h3>Nova Postura</h3>
                        <form onSubmit={handleCreatePosture} className={styles.form}>
                            <label>Macho</label>
                            <select value={postureForm.male} onChange={(e) => setPostureForm({...postureForm, male: e.target.value})}>
                                <option value="">Selecione o Macho</option>
                                {availableBirds.filter(b => b.gender === 'male').map(b => (
                                    <option key={b._id} value={b._id}>{b.name} ({b.bird_band_left} - {b.bird_band_right})</option>
                                ))}
                            </select>

                            <label>Fêmea</label>
                            <select value={postureForm.female} onChange={(e) => setPostureForm({...postureForm, female: e.target.value})}>
                                <option value="">Selecione a Fêmea</option>
                                {availableBirds.filter(b => b.gender === 'female').map(b => (
                                    <option key={b._id} value={b._id}>{b.name} ({b.bird_band_left} - {b.bird_band_right})</option>
                                ))}
                            </select>

                            <label>Data de Início *</label>
                            <input 
                                type="date" 
                                value={postureForm.posture_start} 
                                onChange={(e) => setPostureForm({...postureForm, posture_start: e.target.value})} 
                                required 
                            />

                            <div className={styles.modal_actions}>
                                <button type="button" onClick={() => { resetPostureForm(); setIsPostureModalOpen(false); }} className={styles.btn_cancel}>Cancelar</button>
                                <button type="submit" className={styles.btn_submit}>Salvar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isFinishModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content} style={{ maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3>Finalizar Postura e Registrar Filhotes</h3>
                        <form onSubmit={handleFinishSubmit} className={styles.form}>
                            <label>Data de Término da Postura:
                                <input type="date" value={finishFormData.posture_end} onChange={(e) => setFinishFormData({ ...finishFormData, posture_end: e.target.value })} required />
                            </label>
                            <label>Quantidade Total de Ovos:
                                <input type="number" min="0" value={finishFormData.number_eggs} onChange={(e) => setFinishFormData({ ...finishFormData, number_eggs: e.target.value })} />
                            </label>
                            <label>Observações/Comentários:
                                <input type="text" value={finishFormData.comments} onChange={(e) => setFinishFormData({ ...finishFormData, comments: e.target.value })} />
                            </label>

                            <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />

                            <h4>Filhotes Nascidos</h4>
                            <label>Quantos filhotes nasceram nesta postura?
                                <input type="number" min="0" value={offspringCount} onChange={(e) => handleOffspringCountChange(e.target.value)} />
                            </label>

                            {offspringList.map((child, index) => (
                                <div key={index} style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', marginTop: '10px', border: '1px solid #e5e7eb' }}>
                                    <h5>🐥 Filhote #{index + 1}</h5>
                                    <div className={styles.offspring_form_grid}>
                                        <label>Nome: <input type="text" value={child.name} onChange={(e) => handleOffspringChange(index, 'name', e.target.value)} /></label>
                                        <label>Anilha Esquerda: <input type="text" value={child.bird_band_left} onChange={(e) => handleOffspringChange(index, 'bird_band_left', e.target.value)} /></label>
                                        <label>Anilha Direita: <input type="text" value={child.bird_band_right} onChange={(e) => handleOffspringChange(index, 'bird_band_right', e.target.value)} /></label>
                                        <label>Raça: <input type="text" value={child.race} onChange={(e) => handleOffspringChange(index, 'race', e.target.value)} /></label>
                                        <label>Cor: <input type="text" value={child.color} onChange={(e) => handleOffspringChange(index, 'color', e.target.value)} /></label>
                                        <label>Sexo:
                                            <select value={child.gender} onChange={(e) => handleOffspringChange(index, 'gender', e.target.value)}>
                                                <option value="undefined">Indefinido</option>
                                                <option value="male">Macho</option>
                                                <option value="female">Fêmea</option>
                                            </select>
                                        </label>
                                        <label>Data Nascimento: <input type="date" value={child.birthday} onChange={(e) => handleOffspringChange(index, 'birthday', e.target.value)} required /></label>
                                    </div>
                                </div>
                            ))}

                            <div className={styles.modal_actions}>
                                <button type="button" className={styles.btn_cancel} onClick={() => setIsFinishModalOpen(false)}>Cancelar</button>
                                <button type="submit" className={styles.btn_submit}>Salvar e Encerrar Postura</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CageProfile;